import {
  Home,
  Calendar,
  UserPlus,
  UserSearch,
  BarChart4,
  UserRoundPlus,
  CalendarPlus,
  CircleDollarSign,
  DollarSign,
  BookText,
  Package,
  KeySquare,
  FileSignature,
  Mail,
  CreditCard,
  Fingerprint,
} from 'lucide-react';
import { type LucideIcon } from 'lucide-react';
import { UserRole } from '@entities/auth';

export const quickLinks = {
  aws_console: 'https://d-9267b650a9.awsapps.com/start/',
  cognito:
    'https://eclipsefitlv.auth.us-west-2.amazoncognito.com/login?client_id=10pl1ei222ole2dk8obospnp2j&response_type=code&scope=email+openid&redirect_uri=https%3A%2F%2Fwww.eclipsefitnesslv.com%2F', // Trainers
  docuseal: 'https://docuseal.co/', // Admin
  zohomail: 'https://zohomail.com/', // Admin
  stripe: 'https://stripe.com/', // Trainers
  kisi: 'https://web.kisi.io/users/sign_in', // Trainers
} as const;
export type QuickLinks = keyof typeof quickLinks;

export const NEW_TAB_NAV_ITEM_TITLES = [
  'Documentation',
  'AWS Console',
  'Cognito',
  'Docuseal',
  'Zohomail',
  'Stripe',
  'Kisi',
] as NavItem['title'][];

// Nav Items Start Here
export type NavItem = {
  title: string;
  href: string;
  icon: LucideIcon;
  color?: string;
  isChidren?: boolean;
  children?: NavItem[];
  endOfSection?: boolean; // Put this at the end of sections to render a separator
  accessibleToRoles: UserRole[];
  memberStatus?: 'active' | 'inactive';
};

export const NavItems: NavItem[] = [
  {
    title: 'Home',
    icon: Home,
    href: '/dashboard',
    color: 'text-primary',
    endOfSection: true,
    accessibleToRoles: ['admin', 'trainer'],
  },
  {
    title: 'Trainer Sign-Up',
    icon: UserPlus,
    href: '/trainer-signup',
    color: 'text-primary',
    accessibleToRoles: ['admin'],
  },
  {
    title: 'Users Management',
    icon: UserSearch,
    href: '/users-management',
    color: 'text-primary',
    endOfSection: true,
    accessibleToRoles: ['admin', 'trainer'],
  },
  {
    title: 'Create Member',
    icon: UserRoundPlus,
    href: '/admin-create-user',
    color: 'text-primary',
    accessibleToRoles: ['admin'],
  },
  {
    title: 'Analytics',
    icon: BarChart4,
    href: '/analytics',
    color: 'text-primary',
    accessibleToRoles: ['admin'],
  },
  {
    title: 'Calendar',
    icon: Calendar,
    href: '/event-calendar',
    color: 'text-primary',
    accessibleToRoles: ['admin', 'trainer', 'member'],
    memberStatus: 'active',
  },
  {
    title: 'Event Management',
    icon: CalendarPlus,
    href: '/event-management',
    color: 'text-primary',
    accessibleToRoles: ['admin', 'trainer'],
    endOfSection: true,
  },
  {
    title: 'Class Vouchers',
    icon: CircleDollarSign,
    href: 'https://buy.stripe.com/6oEcQo8Ra9Ingco6oo',
    color: 'text-primary',
    accessibleToRoles: ['admin', 'trainer', 'member'],
    memberStatus: 'active',
  },
  {
    title: 'PT Vouchers',
    icon: DollarSign,
    href: '',
    color: 'text-primary',
    accessibleToRoles: ['admin', 'trainer'],
    endOfSection: true,
  },
  {
    title: 'Documentation',
    icon: BookText,
    href: 'https://eclipsefitnesslv.atlassian.net/wiki/spaces/EFLD/pages/51871745/User+Guides',
    color: 'text-primary',
    accessibleToRoles: ['admin', 'trainer'],
    endOfSection: true,
  },
  {
    title: 'AWS Console',
    icon: Package,
    href: quickLinks.aws_console,
    color: 'text-primary',
    accessibleToRoles: ['admin'],
  },
  {
    title: 'Cognito',
    icon: KeySquare,
    href: quickLinks.cognito,
    color: 'text-primary',
    accessibleToRoles: ['admin', 'trainer'],
  },
  {
    title: 'Docuseal',
    icon: FileSignature,
    href: quickLinks.docuseal,
    color: 'text-primary',
    accessibleToRoles: ['admin'],
  },
  {
    title: 'Zohomail',
    icon: Mail,
    href: quickLinks.zohomail,
    color: 'text-primary',
    accessibleToRoles: ['admin'],
  },
  {
    title: 'Stripe',
    icon: CreditCard,
    href: quickLinks.stripe,
    color: 'text-primary',
    accessibleToRoles: ['admin', 'trainer'],
  },
  {
    title: 'Kisi',
    icon: Fingerprint,
    href: quickLinks.kisi,
    color: 'text-primary',
    accessibleToRoles: ['admin'],
  },
];

export const isStripeLink = (navItemTitles: string) => {
  return navItemTitles.split(' ').includes('Vouchers');
};

export const isNewTabItemTitle = <TObj extends NavItem>(
  title: TObj['title']
): boolean => {
  return NEW_TAB_NAV_ITEM_TITLES.includes(title);
};

export const getNavItemsForRole = (
  role: UserRole,
  trainerStripeLinks?: string,
  memberStatus?: 'active' | 'inactive'
) => {
  //Filter navItems by role
  const navItems = NavItems.filter((nav) =>
    nav.accessibleToRoles.includes(role)
  );

  console.debug(memberStatus);

  return navItems.map((items) => {
    //For Trainers personal stripe links
    const href =
      items.title === 'PT Vouchers'
        ? trainerStripeLinks ?? '/'
        : isNewTabItemTitle(items.title) || isStripeLink(items.title)
          ? items.href
          : items.title === 'Class Vouchers'
            ? items.href
            : `${role === 'member' ? '' : role}${items.href}`;

    /*
    if (items.title) === 'PT Vouchers' {
      href = tranerStripeLinks ?? '/'
    }
    else if (isNavTabItemTitle(item.title) || isStripeLink(items.title)) {
      href = items.href
    }
    else if (items.title === 'Class Vouchers') {
      href = items.href
      // Only if user is a member and their memberStatus is inactive
    } if (role === 'member' && memberStatus !== 'inactive' ||) {
       href = `${role === 'member' ? '' : role}${items.href}`
    }

    
    
    
    */

    return {
      ...items,
      href: href,
    };
  });
};
